<script setup>
import { computed, h } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import { messageStamp, dynamicTime } from 'shared/helpers/timeHelper';

import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import UserAvatarWithName from 'dashboard/components/widgets/UserAvatarWithName.vue';
import ConversationCell from '../ConversationCell.vue';

import { CSAT_RATINGS } from 'shared/constants/messages';

import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';

const props = defineProps({
  pageIndex: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['pageChange']);
const { t } = useI18n();

const csatResponses = useMapGetter('csat/getCSATResponses');
const metrics = useMapGetter('csat/getMetrics');

const tableData = computed(() =>
  csatResponses.value.map(response => ({
    contact: response.contact,
    assignedAgent: response.assigned_agent,
    rating: response.rating,
    problemSolved: response.problem_solved,
    feedbackText: response.feedback_message || '---',
    conversationId: response.conversation_id,
    createdAgo: dynamicTime(response.created_at),
    createdAt: messageStamp(response.created_at, 'LLL d yyyy, h:mm a'),
  }))
);

const columnHelper = createColumnHelper();

const columns = computed(() => [
  columnHelper.accessor('rating', {
    header: t('CSAT_REPORTS.TABLE.HEADER.RATING'),
    align: 'center',
    width: 80,
    cell: cellProps => {
      const { rating: giveRating } = cellProps.row.original;
      const [ratingObject = {}] = CSAT_RATINGS.filter(
        r => r.value === giveRating
      );

      return h(
        'span',
        {
          class: ratingObject.emoji
            ? 'emoji-response text-lg'
            : 'text-n-slate-10',
        },
        ratingObject.emoji || '---'
      );
    },
  }),
  columnHelper.accessor('problemSolved', {
    header: t('CSAT_REPORTS.TABLE.HEADER.PROBLEM_SOLVED'),
    align: 'center',
    width: 120,
    cell: cellProps => {
      const solved = cellProps.row.original.problemSolved;
      if (solved === null || solved === undefined) {
        return h('span', { class: 'text-n-slate-10' }, '---');
      }
      return h(
        'span',
        {
          class: solved
            ? 'inline-flex items-center gap-1 px-2 py-1 rounded-md bg-green-600 text-white text-xs font-medium'
            : 'inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-600 text-white text-xs font-medium',
        },
        [
          h('span', {}, solved ? '👍' : '👎'),
          h(
            'span',
            {},
            solved
              ? t('CSAT_REPORTS.TABLE.PROBLEM_SOLVED_YES')
              : t('CSAT_REPORTS.TABLE.PROBLEM_SOLVED_NO')
          ),
        ]
      );
    },
  }),
  columnHelper.accessor('assignedAgent', {
    header: t('CSAT_REPORTS.TABLE.HEADER.AGENT_NAME'),
    width: 200,
    cell: cellProps => {
      const { assignedAgent } = cellProps.row.original;
      if (assignedAgent) {
        return h(UserAvatarWithName, {
          user: assignedAgent,
          class: 'max-w-[200px] overflow-hidden',
        });
      }
      return '--';
    },
  }),
  columnHelper.accessor('feedbackText', {
    header: t('CSAT_REPORTS.TABLE.HEADER.FEEDBACK_TEXT'),
    width: 400,
    cell: cellProps => {
      const value = cellProps.getValue() || '---';
      return h(
        'span',
        {
          class: 'line-clamp-5 break-words max-w-full text-n-slate-12',
          title: value,
        },
        value
      );
    },
  }),
  columnHelper.accessor('conversationId', {
    header: '',
    width: 100,
    cell: cellProps => h(ConversationCell, cellProps),
  }),
]);

const paginationParams = computed(() => ({
  pageIndex: props.pageIndex,
  pageSize: 15,
}));

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  manualPagination: true,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return metrics.value.totalResponseCount;
  },
  state: {
    get pagination() {
      return paginationParams.value;
    },
  },
  onPaginationChange: updater => {
    const newPagination = updater(paginationParams.value);
    emit('pageChange', newPagination.pageIndex);
  },
});
</script>

<template>
  <div>
    <Table :table="table" class="max-h-[calc(100vh-21.875rem)]" />
    <div
      v-show="!tableData.length"
      class="h-48 flex items-center justify-center text-n-slate-12 text-sm"
    >
      {{ $t('CSAT_REPORTS.NO_RECORDS') }}
    </div>
    <div v-if="metrics.totalResponseCount" class="mt-2">
      <Pagination :table="table" />
    </div>
  </div>
</template>
